// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { TaskService } from '../services/task.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as TaskActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { TASK_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/task';


/**
 * This effect is for listen Task actions, execute Task service call and dispatch in Task reducer.
 * For Task api call purpose.
 *
 * @author Nirmal Jodhani <nirmal.jodhani@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 19/05/2020 (Nirmal Jodhani <nirmal.jodhani@saeculumsolutions.com>)  created Task effect
 */
@Injectable()
export class TaskEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private taskService: TaskService) { }

  /**
   * @param type : Task action name.
      * Task Effect
      * @param payload : object to identify common Task method base on module and method.
      * @returns Empty action dispatch to reducer.
      */
  TaskCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<TaskActions.TaskCommonAction>(TaskActions.ActionTypes.TASK_COMMON),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = TASK_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (state[common.STATE]) {
        return of(new globalActions.EmptyAction({}));
      }
      // Only sending payload data
  
      return this.taskService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // 204 No Content → HttpClient parses empty body as null. Treat as empty success.
            if (data == null) {
              action.payload.callback({ success: true, data: get(data, common.API_RESPONSE), response: get(data, common.API_RESPONSE) });
              return [new globalActions.EmptyAction({})];
            }
            // throwing an error on if the status is not 200
            if (data.status !== 200) { return throwError(() => data); }
            let actions: any[] = [];


            // dispatch Task success action
            actions = [new TaskActions.TaskCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: TaskActions.ActionTypes.TASK_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`, response: data,
              }));
            } else {
              action.payload.callback({ success: true, data: get(data, common.API_RESPONSE), response: get(data, common.API_RESPONSE) });
            }

            return actions;
          }),
          catchError(error =>
              of(new globalActions.PopupNotificationAction({
              actionType: TaskActions.ActionTypes.TASK_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }))
          ));
    }),
  ));
}
